import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from "@/App";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import {
  Sparkles, Send, MapPin, Calendar, Users, Loader2,
  Map, MessageCircle, Plane, ChevronRight, ArrowLeft,
  BookmarkCheck, DollarSign, Bed, Utensils, Ticket
} from "lucide-react";

export default function Planner() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("itinerary");
  
  return (
    <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="planner-page">
      {/* Header */}
      <header className="bg-gradient-to-br from-amber-400 to-amber-500 text-white px-6 pt-8 pb-6 rounded-b-3xl">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-1 text-amber-100 mb-3 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Back</span>
        </button>
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-8 h-8" />
          <h1 className="text-2xl font-bold">AI Travel Planner</h1>
        </div>
        <p className="text-amber-100">Let AI help you plan your perfect trip</p>
      </header>
      
      <main className="px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 rounded-full bg-slate-100 p-1 mb-6">
            <TabsTrigger 
              value="itinerary" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <Map className="w-4 h-4 mr-2" /> Itinerary
            </TabsTrigger>
            <TabsTrigger 
              value="coach" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> Travel Coach
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="itinerary">
            <ItineraryGenerator />
          </TabsContent>
          
          <TabsContent value="coach">
            <TravelCoach />
          </TabsContent>
        </Tabs>
      </main>
      
      <BottomNav />
    </div>
  );
}

function TripCostEstimator({ destination, duration, popularPrice }) {
  const days = parseInt(duration) || 5;
  const flightEst = popularPrice ? Math.round(popularPrice * 0.45) : Math.round(days * 60 + 200);
  const [flight, setFlight] = useState(flightEst);
  const [hotel, setHotel] = useState(Math.round(days * 85));
  const [food, setFood] = useState(Math.round(days * 45));
  const [activities, setActivities] = useState(Math.round(days * 35));
  const total = flight + hotel + food + activities;

  return (
    <div className="savecation-card mt-4" data-testid="cost-estimator">
      <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
        <DollarSign className="w-4 h-4 text-emerald-500" /> Estimated Trip Cost
      </h3>
      <div className="space-y-2 mb-3">
        {[
          { label: "Flights", icon: Plane, value: flight, setValue: setFlight },
          { label: "Hotel", icon: Bed, value: hotel, setValue: setHotel },
          { label: "Food", icon: Utensils, value: food, setValue: setFood },
          { label: "Activities", icon: Ticket, value: activities, setValue: setActivities },
        ].map(({ label, icon: Icon, value, setValue }) => (
          <div key={label} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-sm text-slate-600 w-28">
              <Icon className="w-3.5 h-3.5 text-slate-400" /> {label}
            </span>
            <div className="flex items-center gap-1">
              <span className="text-slate-400 text-sm">$</span>
              <input
                type="number"
                value={value}
                onChange={(e) => setValue(Math.max(0, parseInt(e.target.value) || 0))}
                className="w-20 text-right text-sm font-medium text-slate-800 border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-sky-300"
              />
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between items-center pt-3 border-t border-slate-100">
        <span className="font-bold text-slate-700">Total</span>
        <span className="text-xl font-extrabold text-emerald-600">${total.toLocaleString()}</span>
      </div>
      <p className="text-xs text-slate-400 mt-1">Tap any amount to edit your estimate.</p>
    </div>
  );
}

function ItineraryGenerator() {
  const [destination, setDestination] = useState("");
  const [duration, setDuration] = useState("5");
  const [interests, setInterests] = useState([]);
  const [itinerary, setItinerary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [selectedDestPrice, setSelectedDestPrice] = useState(null);
  
  const interestOptions = ["Food & Dining", "History", "Nature", "Art & Museums", "Shopping", "Nightlife", "Adventure", "Family"];
  
  const popularDestinations = [
    { name: "Paris, France", price: 850, duration: 5, image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400&q=80", tag: "Trending" },
    { name: "Tokyo, Japan", price: 1200, duration: 7, image: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&q=80", tag: "Popular" },
    { name: "Bali, Indonesia", price: 650, duration: 6, image: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=400&q=80", tag: "Budget" },
    { name: "Barcelona, Spain", price: 750, duration: 4, image: "https://images.unsplash.com/photo-1583422409516-2895a77efded?w=400&q=80", tag: "Hot" },
    { name: "Cancun, Mexico", price: 550, duration: 5, image: "https://images.unsplash.com/photo-1552074284-5e88ef1aef18?w=400&q=80", tag: "Deal" },
    { name: "Santorini, Greece", price: 900, duration: 4, image: "https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=400&q=80", tag: "Romantic" },
  ];
  
  const selectDestination = (dest) => {
    setDestination(dest.name);
    setDuration(dest.duration.toString());
    setSelectedDestPrice(dest.price);
    setShowForm(true);
  };
  
  const toggleInterest = (interest) => {
    setInterests(prev => 
      prev.includes(interest) 
        ? prev.filter(i => i !== interest)
        : [...prev, interest]
    );
  };
  
  const handleGenerate = async () => {
    if (!destination) {
      toast.error("Please enter a destination");
      return;
    }
    
    setIsLoading(true);
    setItinerary("");
    
    try {
      const response = await fetch(`${API}/ai/itinerary`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          destination,
          duration_days: parseInt(duration),
          interests
        })
      });
      
      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error("Server error. Please try again.");
      }
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to generate itinerary");
      }
      
      if (data.itinerary) {
        setItinerary(data.itinerary);
        autoSaveItinerary(destination, duration, interests, data.itinerary);
      } else {
        throw new Error("No itinerary received");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const autoSaveItinerary = async (dest, days, tags, text) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await fetch(`${API}/ai/itineraries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ destination: dest, duration_days: parseInt(days), interests: tags, itinerary: text }),
      });
      if (res.ok) {
        const saved = await res.json();
        setSavedId(saved.id || saved.itinerary_id || true);
        toast.success("Itinerary saved to your profile!", { icon: "🔖" });
      }
    } catch {
      // non-critical — don't surface to user
    }
  };
  
  const tagColors = {
    "Trending": "bg-sky-100 text-sky-700",
    "Popular": "bg-violet-100 text-violet-700",
    "Budget": "bg-emerald-100 text-emerald-700",
    "Hot": "bg-red-100 text-red-700",
    "Deal": "bg-amber-100 text-amber-700",
    "Romantic": "bg-pink-100 text-pink-700",
  };
  
  return (
    <div className="space-y-6 mb-24">
      {!itinerary ? (
        <>
          {/* Popular Destinations */}
          {!showForm && (
            <section data-testid="popular-destinations">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Popular Destinations</h2>
              <p className="text-sm text-slate-500 mb-4">Tap a destination to start planning</p>
              <div className="grid grid-cols-2 gap-3">
                {popularDestinations.map((dest) => (
                  <button
                    key={dest.name}
                    onClick={() => selectDestination(dest)}
                    className="group relative rounded-2xl overflow-hidden aspect-[4/3] text-left shadow-sm hover:shadow-md transition-shadow"
                    data-testid={`dest-${dest.name.split(',')[0].toLowerCase()}`}
                  >
                    <img 
                      src={dest.image} 
                      alt={dest.name} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute top-2 left-2">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${tagColors[dest.tag]}`}>
                        {dest.tag}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-white font-bold text-sm leading-tight">{dest.name}</p>
                      <p className="text-white/80 text-xs mt-0.5">From <span className="text-emerald-300 font-bold">${dest.price}</span> · {dest.duration} days</p>
                    </div>
                  </button>
                ))}
              </div>
              
              <div className="mt-4 text-center">
                <Button 
                  variant="outline"
                  onClick={() => setShowForm(true)}
                  className="rounded-full px-6"
                  data-testid="custom-destination-btn"
                >
                  <MapPin className="w-4 h-4 mr-2" /> Enter a custom destination
                </Button>
              </div>
            </section>
          )}
          
          {/* Form */}
          {showForm && (
            <div className="savecation-card" data-testid="itinerary-form">
              {!destination && (
                <button 
                  onClick={() => setShowForm(false)} 
                  className="flex items-center gap-1 text-sky-500 text-sm mb-3 hover:text-sky-600"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to destinations
                </button>
              )}
              <h2 className="text-lg font-bold text-slate-900 mb-4">Plan your trip</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Destination</label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input 
                      placeholder="e.g., Paris, France"
                      value={destination}
                      onChange={(e) => setDestination(e.target.value)}
                      className="savecation-input pl-12"
                      data-testid="destination-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Duration (days)</label>
                  <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input 
                      type="number"
                      min="1"
                      max="30"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      className="savecation-input pl-12"
                      data-testid="duration-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Interests (optional)</label>
                  <div className="flex flex-wrap gap-2">
                    {interestOptions.map(interest => (
                      <button
                        key={interest}
                        onClick={() => toggleInterest(interest)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          interests.includes(interest)
                            ? "bg-sky-500 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                        data-testid={`interest-${interest.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {interest}
                      </button>
                    ))}
                  </div>
                </div>
                
                <Button 
                  onClick={handleGenerate}
                  disabled={isLoading}
                  className="savecation-button w-full"
                  data-testid="generate-itinerary-btn"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Creating your itinerary...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      Generate Itinerary
                    </span>
                  )}
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Plane className="w-5 h-5 text-sky-500" /> {destination}
              </h2>
              <p className="text-sm text-slate-500 flex items-center gap-1.5">
                {duration}-day itinerary
                {savedId && (
                  <span className="inline-flex items-center gap-0.5 text-emerald-600 font-medium text-xs bg-emerald-50 px-2 py-0.5 rounded-full">
                    <BookmarkCheck className="w-3 h-3" /> Saved
                  </span>
                )}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => { setItinerary(null); setShowForm(false); setDestination(""); setSavedId(null); setSelectedDestPrice(null); }}
              className="rounded-full"
              data-testid="new-trip-btn"
            >
              New Trip
            </Button>
          </div>

          <TripCostEstimator
            destination={destination}
            duration={duration}
            popularPrice={selectedDestPrice}
          />

          <div className="savecation-card">
            <div className="prose prose-slate max-w-none whitespace-pre-wrap text-sm leading-relaxed">
              {itinerary}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TravelCoach() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hey there! I'm Sunny, your travel coach! ☀️\n\nI can help you with:\n• Destination recommendations\n• Budget planning tips\n• Packing suggestions\n• Travel safety advice\n• Local customs and etiquette\n\nWhat would you like to know?"
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API}/ai/coach`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: userMessage,
          conversation_id: conversationId
        })
      });
      
      // Parse response safely
      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error("Invalid response from server");
      }
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to get response");
      }
      
      if (data.conversation_id) {
        setConversationId(data.conversation_id);
      }
      setMessages(prev => [...prev, { role: "assistant", content: data.response || "I couldn't generate a response." }]);
    } catch (error) {
      toast.error(error.message);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Oops! Something went wrong. Please try again." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="flex flex-col h-[calc(100vh-20rem)] mb-24">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4">
        {messages.map((msg, index) => (
          <div 
            key={index}
            className={`chat-message ${msg.role}`}
            data-testid={`message-${index}`}
          >
            <p className="whitespace-pre-wrap">{msg.content}</p>
          </div>
        ))}
        {isLoading && (
          <div className="chat-message assistant">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Thinking...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input */}
      <div className="flex gap-2">
        <Input 
          placeholder="Ask me anything about travel..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSend()}
          className="savecation-input flex-1"
          data-testid="coach-input"
        />
        <Button 
          onClick={handleSend}
          disabled={isLoading || !input.trim()}
          className="savecation-button w-12 h-12 p-0"
          data-testid="send-message-btn"
        >
          <Send className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}
